import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "../forms/FormField";
import { useGetRegion } from "../../hooks/Query/getRegion";
import { useGetZones } from "../../hooks/Query/getZones";
import { useGetStates } from "../../hooks/Query/getState";
import { useGetCities } from "../../hooks/Query/useGetCities";
import { useGetBranches } from "../../hooks/Query/useGetBranches";
import { useGetSegements } from "../../hooks/Query/useGetSegements";
import { useGetMembersEnrollmentReport } from "../../hooks/Query/useGetMembersEnrollmentReport";
import { TailwindSpinner } from "../Spinner/Spinner";
import MemberEnrollmentTable from "./MemberEnrolmentTable";
import {
  MembershipTableColumns,
  MembershipTableSummaryColumns,
} from "../Table/TableColums";
import { useDownloadMembershipReport } from "../../hooks/Query/useDownloadMembershipReport";
import { toast } from "react-toastify";
import { DownloadModal } from "../DownloadModal";
import { downloadFile } from "../../utils/downloadFile";

const validationSchema = Yup.object({
  fromDate: Yup.date().required("From date is required"),
  tillDate: Yup.date().required("Till date is required"),
  country: Yup.string().required("Country is required"),
  region: Yup.string(),
  zone: Yup.string(),
  state: Yup.string(),
  city: Yup.string(),
  branch: Yup.string(),
  segment: Yup.string(),
  tier: Yup.string(),
  membershipId: Yup.string(),
  view: Yup.string().required("View is required"),
  limit: Yup.string(),
  loginStatus:Yup.string(),
});

const initialValues = {
  fromDate: "",
  tillDate: "",
  country: "",
  region: "",
  zone: "",
  state: "",
  city: "",
  branch: "",
  segment: "",
  tier: "",
  membershipId: "",
  view: "",
  limit: "",
  loginStatus:"",
};

const countries = [{ value: 1, label: "Nigeria" }];

const downloadOptions = [
  { value: "100000", label: "100,000" },
  { value: "200000", label: "200,000" },
  { value: "500000", label: "500,000" },
  { value: "1000000", label: "1,000,000" },
  { value: "All", label: "All" },
];

const viewOptions = [
  { value: "1", label: "Detail" },
  { value: "2", label: "Summary" },
];

const viewLogin = [
  { value: "1", label: "Login" },
  { value: "2", label: "Not Login" },
];

const MemberEnrollmentForm = () => {
  const formikRef = useRef();
  const [formData, setFormData] = useState(initialValues);
  const [showDownloadOption, setShowDownloadOption] = useState(false);
  const [payloadData, setPayloadData] = useState();
  const [downloadPayloadData, setDownloadPayloadData] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [totalPage, setTotalPage] = useState(0);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("");
  const [membershipEnrollmentReportData, setMembershipEnrollmentReportData] =
    useState([]);
  const [page, setPage] = useState(1);

  // Data fetching hooks
  const { data: regionsData } = useGetRegion(formData?.country);
  const { data: zonesData } = useGetZones(formData?.region);
  const { data: statesData } = useGetStates(formData?.zone);
  const { data: citiesData } = useGetCities(formData?.state);
  const { data: branchesData } = useGetBranches();
  const { data: segmentsData } = useGetSegements();

  const { data: downloadMembershipReport, isLoading: loadingDownload } =
    useDownloadMembershipReport(
      { payload: downloadPayloadData },
      {
        enabled: Boolean(downloadPayloadData),
        onSuccess: (data) => {
         
        },
        onError: (error) => {
          console.log(error, "error123");
          toast.error("An error occurred while downloading the report.");
          setShouldDownload(false);
        }
      }
    );

    console.log("I AM IN MEMBERSHIP ENROLLMENT FORM");

  const {
    data: membersEnrollmentReport,
    isFetching: loadingReport,
    refetch,
  } = useGetMembersEnrollmentReport(
    {
      payload: payloadData,
      page,
    },
    {
      enabled: Boolean(payloadData),
    }
  );

  // Memoized options
  const regions = useMemo(
    () =>
      regionsData?.data?.data?.map((region) => ({
        value: region?.Region_id,
        label: region?.region_name
          .toLowerCase()
          .replace(/^\w/, (c) => c.toUpperCase()),
      })) || [],
    [regionsData]
  );

  const zones = useMemo(
    () =>
      zonesData?.data?.data?.map((zone) => ({
        value: zone?.Zone_id,
        label: zone?.Zone_name.toLowerCase().replace(/^\w/, (c) =>
          c.toUpperCase()
        ),
      })) || [],
    [zonesData]
  );

  const states = useMemo(
    () =>
      statesData?.data?.states?.map((state) => ({
        value: state?.State_id,
        label: state?.State_name.toLowerCase().replace(/^\w/, (c) =>
          c.toUpperCase()
        ),
      })) || [],
    [statesData]
  );

  const cities = useMemo(
    () =>
      citiesData?.data?.cities?.map((city) => ({
        value: city?.City_id,
        label: city?.City_name.toLowerCase().replace(/^\w/, (c) =>
          c.toUpperCase()
        ),
      })) || [],
    [citiesData]
  );

  const branches = useMemo(
    () =>
      branchesData?.data?.data?.map((branch) => ({
        value: branch?.Branch_code,
        label: branch?.Branch_name.toLowerCase().replace(/^\w/, (c) =>
          c.toUpperCase()
        ),
      })) || [],
    [branchesData]
  );

  const segments = useMemo(
    () =>
      segmentsData?.data?.data?.map((segment) => ({
        value: segment?.Segment_code,
        label: segment?.Segment_name.toLowerCase().replace(/^\w/, (c) =>
          c.toUpperCase()
        ),
      })) || [],
    [segmentsData]
  );

  // Sync formData with Formik values
  useEffect(() => {
    const interval = setInterval(() => {
      if (formikRef.current?.values) {
        setFormData(formikRef.current.values);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  // Show download modal if limit is "All"
  useEffect(() => {
    if (formData?.limit === "All") {
      setOpenModal(true);
      setShowDownloadOption(false);
    }
  }, [formData]);

  // Update table data on report fetch
  useEffect(() => {
    if (!loadingReport && membersEnrollmentReport) {
      setMembershipEnrollmentReportData(
        membersEnrollmentReport?.data?.data?.data || []
      );
      setTotalPage(membersEnrollmentReport?.data?.data?.total);
    } else {
      setMembershipEnrollmentReportData([]);
    }
  }, [membersEnrollmentReport, loadingReport]);

  // Download logic
  useEffect(() => {
    if (shouldDownload && downloadMembershipReport?.data?.data?.length > 0) {
      downloadFile({
        data: downloadMembershipReport?.data?.data,
        format: downloadFormat,
        fileName: `FirstRewards Members Enrollment Report ${downloadPayloadData?.fromDate} - ${downloadPayloadData?.tillDate}`,
      });
      setShouldDownload(false);
    } else if (
      shouldDownload &&
      !loadingDownload &&
      !downloadMembershipReport?.data?.data?.length
    ) {
      toast.warning("No data available to download.");
      setShouldDownload(false);
    }
  }, [
    downloadMembershipReport,
    shouldDownload,
    downloadFormat,
    loadingDownload,
  ]);

  // Handlers
  const handleSubmit = useCallback((values) => {
    setPayloadData(values);
    setPage(1);
  }, []);

  const handleShowDownloadReport = useCallback((values) => {
    if (!values.fromDate || !values.tillDate || !values.country) {
      toast.error("Kindly fill all required fields.");
      return;
    }
    setShowDownloadOption((prev) => !prev);
  }, []);

  const handlePageChange = useCallback(
    (pageNumber) => {
      setPage(pageNumber);
      refetch();
    },
    [refetch]
  );

  const handleDownloadReport = useCallback((values, format) => {
    if (!values) {
      toast.error("An error occurred");
      return;
    }
    setDownloadPayloadData(values);
    setDownloadFormat(format);
    setShouldDownload(true);
  }, []);

  const payload = useMemo(
    () =>
      `from_date=${formData?.fromDate}&to_date=${formData?.tillDate}&country_id=${formData?.country}&view=${formData?.view}&country=${formData?.country}&login=${formData?.loginStatus}`,
    [formData]
  );

   const [selectedRows, setSelectedRows] = useState([]);
  // const [selectedRecords, setSelectedRecords] = useState([]);

  const handleCheck = (rowId, checked) => {
  if (rowId === "all") {
    // Select/unselect all rows on current page
    const pageIds = membershipEnrollmentReportData.map(
      (r) => r.Membership_id
    );
    setSelectedRows((prev) => {
      if (checked) {
        const newSelected = [...prev];
        pageIds.forEach((id) => {
          if (!newSelected.includes(id)) newSelected.push(id);
        });
        return newSelected;
      } else {
        return prev.filter((id) => !pageIds.includes(id));
      }
    });
  } else {
    setSelectedRows((prev) =>
      checked ? [...prev, rowId] : prev.filter((id) => id !== rowId)
    );
  }
};

//   const handleCheck = (row, checked) => {
//   if (row === "all") {
//     // Select all on page
//     const updated = membershipEnrollmentReportData.map((item) => ({
//       ...item,
//       _checked: checked,
//     }));
//     setMembershipEnrollmentReportData(updated);

//     setSelectedRecords(checked ? updated : []);
//   } else {
//     // Update single row
//     const updated = membershipEnrollmentReportData.map((item) =>
//       item.Membership_id === row.Membership_id
//         ? { ...item, _checked: checked }
//         : item
//     );

//     setMembershipEnrollmentReportData(updated);

//     // Update selected list
//     if (checked) {
//       setSelectedRecords((prev) => [...prev, row]);
//     } else {
//       setSelectedRecords((prev) =>
//         prev.filter((x) => x.Membership_id !== row.Membership_id)
//       );
//     }
//   }
// };


  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 bg-white rounded-lg">
      <div className="mb-6">
        <h1 className="text-lg font-medium text-gray-900">Member Enrollment</h1>
        <p className="text-grey-200 text-sm">
          View all enrolled members in the program
        </p>
      </div>

      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form className="w-full relative h-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 md:w-[711px]">
              <FormField
                label="From Date"
                name="fromDate"
                type="date"
                required
              />
              <FormField
                label="Till Date"
                name="tillDate"
                type="date"
                required
              />
              <FormField
                label="Select Country"
                name="country"
                type="select"
                options={countries}
                required
              />
              <FormField
                label="Select Region"
                name="region"
                type="select"
                options={regions}
                disabled={!values.country}
              />
              <FormField
                label="Select Zone"
                name="zone"
                type="select"
                options={zones}
                disabled={!values.region}
              />
              <FormField
                label="Select State"
                name="state"
                type="select"
                options={states}
                disabled={!values.zone}
              />
              <FormField
                label="Select City"
                name="city"
                type="select"
                options={cities}
                disabled={!values.state}
              />
              {/* <FormField label="Select Branch" name="branch" type="select" options={branches} /> */}
              <FormField
                label="Select Segment"
                name="segment"
                type="select"
                options={segments}
              />
              <FormField
                label="Select Tier"
                name="tier"
                type="select"
                options={[]}
              />
              <FormField
                label="Membership ID"
                name="membershipId"
                type="text"
                placeholder="Insert your text here"
              />
              <FormField
                label="Select View"
                name="view"
                type="select"
                options={viewOptions}
                required
              />

              {/* <FormField
                label="Select Login status"
                name="loginStatus"
                type="select"
                options={viewLogin}
                required
              /> */}
              {showDownloadOption && (
                <FormField
                  label="Download Limit"
                  name="limit"
                  type="select"
                  options={downloadOptions}
                />
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-4 mt-6 justify-between items-center">
              <button
                type="submit"
                disabled={loadingReport}
                className="bg-mysecondary hover:bg-blue-800 text-white px-8 py-2 rounded flex items-center justify-center w-[189px] h-[38px] text-sm"
              >
                {loadingReport ? <TailwindSpinner /> : "Search"}
              </button>
              <div>
                <button
                  type="button"
                  className="text-center border-1 border-mysecondary text-blue-900 hover:bg-blue-50 px-8 py-2 rounded flex items-center justify-center w-[189px] h-[38px] text-sm"
                  onClick={() => handleShowDownloadReport(values)}
                >
                  {" Download As"}
                </button>
              </div>
            </div>

{/* <div className="flex justify-end mt-6">
  <div className="flex flex-col md:flex-row items-center gap-4">
    <p>{selectedRows.length} selected</p>
    <button
      className="bg-mysecondary hover:bg-blue-800 text-white px-8 py-2 rounded flex items-center justify-center w-[220px] h-[38px] text-sm"
    >
      Resend enrolment email
    </button>
  </div>
</div> */}


            {showDownloadOption && (
              <div className="flex justify-center md:justify-end">
                <div className="right-0 mt-2 text-center border-1 border-mysecondary text-blue-900 hover:bg-blue-50 rounded flex items-center justify-end w-[189px] flex-col">
                  <button
                    type="button"
                    disabled={loadingDownload}
                    className="border-b border-blue-900 w-full text-sm py-2 hover:bg-mysecondary hover:text-white flex justify-center items-center"
                    onClick={() => handleDownloadReport(values, "pdf")}
                  >
                    {loadingDownload && downloadFormat === "pdf" ? (
                      <TailwindSpinner />
                    ) : (
                      "Pdf"
                    )}
                  </button>
                  <button
                    type="button"
                    className="border-b border-blue-900 w-full text-sm py-2 hover:bg-mysecondary hover:text-white flex justify-center items-center"
                    onClick={() => handleDownloadReport(values, "csv")}
                  >
                    {loadingDownload && downloadFormat === "csv" ? (
                      <TailwindSpinner />
                    ) : (
                      "Csv"
                    )}
                  </button>
                </div>
              </div>
            )}

            {membershipEnrollmentReportData?.length > 0 ? (
              values.view === "2" ? (
                <MemberEnrollmentTable
                  columns={MembershipTableSummaryColumns}
                  data={membershipEnrollmentReportData}
                  page={page}
                  setPage={setPage}
                  handlePageChange={handlePageChange}
                  totalPage={totalPage}
                  loading={loadingReport}
                  title="Members Enrollment"
                  showCheckbox= {false}             // enable checkboxes
                  selectedRows={selectedRows}
                 // selectedRows={selectedRecords}    // pass selected rows
                  onCheck={handleCheck}      
                />
              ) : (
                <MemberEnrollmentTable
                  columns={MembershipTableColumns}
                  data={membershipEnrollmentReportData}
                  page={page}
                  setPage={setPage}
                  handlePageChange={handlePageChange}
                  totalPage={totalPage}
                  loading={loadingReport}
                  title="Members Enrollment"
                  showCheckbox= {false}             // enable checkboxes
                  selectedRows={selectedRows}
                  // selectedRows={selectedRecords}    // pass selected rows
                  onCheck={handleCheck}    
                />
              )
            ) : (
              <div className="text-sm text-center text-gray-500 mt-10">
                No data available for the selected criteria.
              </div>
            )}
            {openModal && (
              <DownloadModal
                setOpenModal={setOpenModal}
                value={formData}
                openModal={openModal}
                payload={payload}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MemberEnrollmentForm;
